import React from 'react';
import { Button } from 'antd';
import { FiCopy, FiEdit3 } from 'react-icons/fi';
import { InPageSpinner } from '@components/spinners/in-page-spinner';
import { TooltipTop } from '@components/tooltips';
import { AirflowDagDetails } from '../../types';
import { formatClockDuration, formatDateTime } from '../../utils';
import { useCodeTab } from './use-code-tab';
import styles from '../../styles.module.scss';

type CodeTabProps = {
    dagId: string;
    details: AirflowDagDetails | null;
    isLoadingDetails: boolean;
    refreshKey: number;
};

/*
 * DAG source code viewer, reused by run/task-instance detail screens — Airflow has no
 * DAG-versioning REST concept, so there's exactly one current source shown, no version selector
 */
export function CodeTab({ dagId, details, isLoadingDetails, refreshKey }: CodeTabProps) {
    const { source, isLoading, handleCopy, editedContent, isSubmitting, handleContentChange, handleSubmit } = useCodeTab(dagId, refreshKey);

    return (
        <div className={styles.card}>
            {!isLoadingDetails && details && (
                <div className={styles.parseMetaBar}>
                    <span>{`Parsed at ${formatDateTime(details.last_parsed ?? details.last_parsed_time)}`}</span>
                    <span>{`Parse duration ${formatClockDuration(details.last_parse_duration)}`}</span>
                </div>
            )}
            <div className={styles.codeToolbar}>
                <span className={styles.codeFilename}>
                    {details?.fileloc ?? `${dagId}.py`}
                    <TooltipTop title="This code is editable">
                        <span className={styles.editableBadge}>
                            <FiEdit3 />
                            Editable
                        </span>
                    </TooltipTop>
                </span>
                <div className={styles.codeToolbarActions}>
                    <Button disabled={!source} onClick={handleCopy}>
                        <FiCopy style={{ marginRight: 6 }} />
                        Copy
                    </Button>
                    <Button type="primary" loading={isSubmitting} disabled={!editedContent} onClick={handleSubmit}>
                        Submit
                    </Button>
                </div>
            </div>
            <div className={styles.codeViewer}>
                {isLoading ? (
                    <InPageSpinner size="Small" />
                ) : (
                    <textarea
                        className={styles.codeTextarea}
                        value={editedContent}
                        onChange={(event) => handleContentChange(event.target.value)}
                        spellCheck={false}
                        wrap="off"
                    />
                )}
            </div>
        </div>
    );
}
